// Typed port of vanilla DB.getShared / setShared / listShared family
// (public/app/index.html L398-746).
//
// Shared-doc prefixes and what they store:
//   team__{code}        → Team metadata (see TeamDoc)
//   uid__{uid}          → UidLookupDoc  (reverse: uid -> email/teamCode)
//   user__{email}       → UserByEmailDoc (email -> uid/teamCode)
//   assign__{id}        → AssignmentDoc  (shared between investor & contractor)
//   portallink__{token} → PortalLinkDoc  (client-facing estimate/portal links)

import {
  collection, deleteDoc, getDoc, getDocs, query, where,
  setDoc, serverTimestamp,
} from 'firebase/firestore'
import { db, sharedDoc } from '../firebase/firestore'
import type {
  TeamDoc, UidLookupSharedDoc, UserByEmailSharedDoc,
  AssignmentDoc, PortalLinkDoc,
} from '../../types/firestore'

// ── Generic shared-doc helpers ─────────────────────────────────────────────

export async function getShared<T>(docId: string): Promise<T | null> {
  const snap = await getDoc(sharedDoc(docId))
  return snap.exists() ? (snap.data() as T) : null
}

/** Recursively drop keys whose value is `undefined` — Firestore rejects
 *  undefined at any depth (throws "Unsupported field value: undefined").
 *  Preserves null, arrays, and nested objects. Safe for plain data. */
function stripUndefinedDeep<T>(value: T): T {
  if (value === null || typeof value !== 'object') return value
  if (Array.isArray(value)) {
    return value.map((v) => stripUndefinedDeep(v)) as unknown as T
  }
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === undefined) continue
    out[k] = stripUndefinedDeep(v)
  }
  return out as T
}

export async function setShared(docId: string, data: Record<string, unknown>): Promise<void> {
  const clean = stripUndefinedDeep(data)
  await setDoc(sharedDoc(docId), { ...clean, _updatedAt: serverTimestamp() }, { merge: true })
}

export async function deleteShared(docId: string): Promise<void> {
  await deleteDoc(sharedDoc(docId))
}

// ── Query by prefix (used for listing) ────────────────────────────────────
// Firestore doesn't support LIKE — we do range queries on the doc id.
// Vanilla uses this pattern to list all "portallink__*" or "assign__*" for a user.
// Implemented via where("__name__" >= prefix, "__name__" < prefix + "\uf8ff")
// but since we can't query by __name__ with `where`, we use orderBy + startAt + endAt.

import { orderBy, startAt, endAt } from 'firebase/firestore'

export async function listSharedByPrefix<T>(prefix: string): Promise<T[]> {
  const col = collection(db, 'shared')
  const q = query(col, orderBy('__name__'), startAt(prefix), endAt(prefix + '\uf8ff'))
  const snap = await getDocs(q)
  return snap.docs.map((d) => d.data() as T)
}

// ── Typed helpers for each prefix ─────────────────────────────────────────

export async function getTeam(code: string): Promise<TeamDoc | null> {
  return getShared<TeamDoc>(`team__${code}`)
}
export async function saveTeam(code: string, data: Partial<TeamDoc>): Promise<void> {
  return setShared(`team__${code}`, data as Record<string, unknown>)
}
export async function deleteTeam(code: string): Promise<void> {
  return deleteShared(`team__${code}`)
}

export async function getUidLookup(uid: string): Promise<UidLookupSharedDoc | null> {
  return getShared<UidLookupSharedDoc>(`uid__${uid}`)
}
export async function saveUidLookup(uid: string, data: Partial<UidLookupSharedDoc>): Promise<void> {
  return setShared(`uid__${uid}`, data as Record<string, unknown>)
}

export async function getUserByEmail(email: string): Promise<UserByEmailSharedDoc | null> {
  return getShared<UserByEmailSharedDoc>(`user__${email.toLowerCase()}`)
}
export async function saveUserByEmail(email: string, data: Partial<UserByEmailSharedDoc>): Promise<void> {
  return setShared(`user__${email.toLowerCase()}`, data as Record<string, unknown>)
}

export async function getAssignment(id: string): Promise<AssignmentDoc | null> {
  return getShared<AssignmentDoc>(`assign__${id}`)
}
export async function saveAssignment(id: string, data: Partial<AssignmentDoc>): Promise<void> {
  return setShared(`assign__${id}`, data as Record<string, unknown>)
}
export async function listMyAssignments(uid: string): Promise<AssignmentDoc[]> {
  const col = collection(db, 'shared')
  const q = query(col, where('participantUids', 'array-contains', uid))
  const snap = await getDocs(q)
  return snap.docs
    .filter((d) => d.id.startsWith('assign__'))
    .map((d) => d.data() as AssignmentDoc)
}

/** Shared-estimate wire shape (V1 partner → us).
 *  Matches public/app/index.html:9669 acceptSharedEstimate data reads. */
export interface SharedEstimateDoc {
  id: string
  fromUid?: string
  fromName?: string
  fromTeamCode?: string
  linkId?: string
  message?: string
  sentAt?: string | number
  respondedAt?: string
  status?: 'pending' | 'accepted' | 'declined' | string
  estimateSnapshot?: {
    id?: number
    name?: string
    clientId?: number
    categories?: Array<{ name: string; items?: Array<{ desc?: string; amount?: number; qty?: number; cost?: number }> }>
    [k: string]: unknown
  }
  participantUids?: string[]
  /** Portal token stamped by the sender at share time. When present the
   *  receiver can open the portal directly without a secondary lookup. */
  portalToken?: string
}

/** List incoming shared estimates visible to `uid`. V1 parity — filters by
 *  estshare__ prefix + excludes ones we sent ourselves. */
export async function listMySharedEstimates(uid: string): Promise<SharedEstimateDoc[]> {
  const col = collection(db, 'shared')
  const q = query(col, where('participantUids', 'array-contains', uid))
  const snap = await getDocs(q)
  return snap.docs
    .filter((d) => d.id.startsWith('estshare__'))
    .map((d) => ({ ...(d.data() as SharedEstimateDoc), id: d.id }))
    .filter((e) => e.fromUid !== uid)
    .filter((e) => (e.status || 'pending') === 'pending')
}

/** List outgoing shared estimates we sent that the recipient has now
 *  accepted or declined — for sender-side feedback (toast + AlertBell). */
export async function listMyOutgoingResponded(uid: string): Promise<SharedEstimateDoc[]> {
  const col = collection(db, 'shared')
  const q = query(col, where('participantUids', 'array-contains', uid))
  const snap = await getDocs(q)
  return snap.docs
    .filter((d) => d.id.startsWith('estshare__'))
    .map((d) => ({ ...(d.data() as SharedEstimateDoc), id: d.id }))
    .filter((e) => e.fromUid === uid)
    .filter((e) => e.status === 'accepted' || e.status === 'declined')
}

export async function getPortalLink(token: string): Promise<PortalLinkDoc | null> {
  return getShared<PortalLinkDoc>(`portallink__${token}`)
}
export async function savePortalLink(token: string, data: Partial<PortalLinkDoc>): Promise<void> {
  return setShared(`portallink__${token}`, data as Record<string, unknown>)
}
// ── Link requests (linkreq__*) ──────────────────────────────────────────────
// Cross-account "please link with me" docs. V1 parity
// (public/app/index.html:9031 sendLinkRequest, :9088 approveLinkRequest).

export interface LinkRequestDoc {
  id: string
  fromUid: string
  fromEmail?: string
  fromName?: string
  fromTeamCode?: string
  fromRole?: 'investor' | 'gc' | 'sub'
  toEmail?: string
  toPhone?: string
  toUid?: string | null
  toName?: string | null
  toRole?: 'investor' | 'gc' | 'sub' | null
  status: 'pending' | 'approved' | 'declined'
  sentAt?: string | number
  expiresAt?: string | number
  respondedAt?: string | null
  dealLinkId?: string | null
  message?: string
  participantUids?: string[]
  clientId?: number
  clientName?: string
}

/** Load all pending link requests visible to this user — both incoming
 *  (toEmail matches) and outgoing (participantUids contains own uid). */
export async function listMyLinkRequests(uid: string, email: string): Promise<{ incoming: LinkRequestDoc[]; outgoing: LinkRequestDoc[] }> {
  const col = collection(db, 'shared')
  const out: LinkRequestDoc[] = []
  const inc: LinkRequestDoc[] = []
  const myEmail = (email || '').toLowerCase()
  try {
    const qSent = query(col, where('participantUids', 'array-contains', uid))
    const snap = await getDocs(qSent)
    for (const d of snap.docs) {
      if (!d.id.startsWith('linkreq__')) continue
      out.push({ ...(d.data() as LinkRequestDoc), id: d.id })
    }
  } catch { /* ignore — rules may deny; treat as empty */ }
  if (myEmail) {
    try {
      const qRecv = query(col, where('toEmail', '==', myEmail))
      const snap = await getDocs(qRecv)
      for (const d of snap.docs) {
        if (!d.id.startsWith('linkreq__')) continue
        inc.push({ ...(d.data() as LinkRequestDoc), id: d.id })
      }
    } catch { /* ignore */ }
  }
  return { incoming: inc, outgoing: out }
}

export interface DealLinkDoc {
  id: string
  initiatorUid: string
  initiatorTeamCode?: string
  initiatorName?: string
  partnerUid: string
  partnerTeamCode?: string
  partnerName?: string
  status: 'active' | 'inactive'
  createdAt: string
  fromRequestId?: string
  side1Role?: 'investor' | 'gc' | 'sub'
  side2Role?: 'investor' | 'gc' | 'sub'
  participantUids: string[]
}

export async function getDealLink(id: string): Promise<DealLinkDoc | null> {
  return getShared<DealLinkDoc>(id)
}

export async function listMyDealLinks(uid: string): Promise<DealLinkDoc[]> {
  const col = collection(db, 'shared')
  const q = query(col, where('participantUids', 'array-contains', uid))
  const snap = await getDocs(q)
  return snap.docs
    .filter((d) => d.id.startsWith('deallink__'))
    .map((d) => ({ ...(d.data() as DealLinkDoc), id: d.id }))
}

/** Find any dealLink for this GC where the partner's email or name matches
 *  the given client. Used as a fallback when client.linkedDealLinkId is not
 *  stamped. Best-effort — returns null if no match. */
export async function findDealLinkForClient(
  uid: string,
  clientEmail: string | undefined,
  clientName: string | undefined,
): Promise<DealLinkDoc | null> {
  const col = collection(db, 'shared')
  const q = query(col, where('participantUids', 'array-contains', uid))
  const snap = await getDocs(q)
  const docs = snap.docs
    .filter((d) => d.id.startsWith('deallink__'))
    .map((d) => ({ ...(d.data() as DealLinkDoc), id: d.id }))
  const emailLC = clientEmail?.toLowerCase().trim() || null
  const nameLC  = clientName?.toLowerCase().trim() || null
  for (const d of docs) {
    const raw = d as unknown as {
      initiatorUid?: string; partnerUid?: string;
      initiatorEmail?: string; partnerEmail?: string;
      initiatorName?: string;  partnerName?: string;
    }
    const otherEmail = (raw.initiatorUid === uid ? raw.partnerEmail : raw.initiatorEmail)?.toLowerCase().trim()
    const otherName  = (raw.initiatorUid === uid ? raw.partnerName  : raw.initiatorName)?.toLowerCase().trim()
    if (emailLC && otherEmail && emailLC === otherEmail) return d
    if (nameLC  && otherName  && nameLC  === otherName)  return d
  }
  return null
}

export async function listMyPortalLinks(uid: string): Promise<PortalLinkDoc[]> {
  const col = collection(db, 'shared')
  const q = query(col, where('ownerUid', '==', uid))
  const snap = await getDocs(q)
  return snap.docs
    .filter((d) => d.id.startsWith('portallink__'))
    .map((d) => d.data() as PortalLinkDoc)
}

/** Find the portallink token for a received project (the receiver doesn't own
 *  the portallink doc — the sending GC does). Returns the token string from
 *  the doc id (format `portallink__<token>`) or null when no match.
 *
 *  Query shape: orderBy('__name__') + startAt/endAt('portallink__') — the
 *  range constraint on the doc id is what lets the Firestore rules static
 *  analyzer prove line 379 (`allow read: if docId.matches('portallink__.*')`)
 *  covers every result. WITHOUT the range constraint, the query runs as an
 *  unfiltered `where('ownerUid','==',fromUid)` against the whole `shared`
 *  collection; the rules engine can't prove every result is a portallink doc,
 *  so it falls back to the PARTICIPANT / OWNER rules (lines 100-109) — which
 *  fail for the receiver since they're neither the portallink owner nor in
 *  participantUids. The receiver's query throws permission-denied and this
 *  helper returns null. That's why the old UI showed "Portal link not
 *  available — ask {fromName} to enable it" even when the GC had generated
 *  a valid link.
 *
 *  The ownerUid match moves to a client-side filter (post-snapshot) — cheap
 *  because the portallink__ prefix already narrows to a small slice.
 *
 *  Used by the "Web portal link" section rendered on received-project detail
 *  so the receiver can copy / open / email the anonymous-client URL without
 *  having to ask the GC for it. */
export async function findPortalTokenForReceivedProject(
  fromUid: string,
  projName: string,
  opts?: { fromProjId?: number | null },
): Promise<string | null> {
  if (!fromUid) return null
  const col = collection(db, 'shared')
  const q = query(
    col,
    orderBy('__name__'),
    startAt('portallink__'),
    endAt('portallink__\uf8ff'),
  )
  const snap = await getDocs(q)
  const wantName = (projName || '').trim().toLowerCase()
  const wantProjId = opts?.fromProjId ?? null
  // Prefer docs with kind/status hints when available (accepted > pending);
  // V1 keeps multiple tokens per estimate but only the accepted/project-linked
  // one is meaningful post-accept.
  type Candidate = {
    token: string
    status?: string
    projId?: number | null
    nameMatch: boolean
    projIdMatch: boolean
  }
  const ownerCandidates: Candidate[] = []
  for (const d of snap.docs) {
    const data = d.data() as {
      ownerUid?: string; projName?: string; status?: string; projId?: number | null
    }
    if (data.ownerUid !== fromUid) continue
    const docName = (data.projName || '').trim().toLowerCase()
    const nameMatch = !!wantName && docName === wantName
    const projIdMatch = wantProjId != null && typeof data.projId === 'number' && data.projId === wantProjId
    const token = d.id.slice('portallink__'.length)
    ownerCandidates.push({
      token,
      ...(data.status ? { status: data.status } : {}),
      projId: data.projId ?? null,
      nameMatch,
      projIdMatch,
    })
  }
  if (ownerCandidates.length === 0) return null
  // Preference order (most-to-least confident):
  //   1. exact projName + status='accepted' + projId set
  //   2. exact projName
  //   3. exact sender-side projId match (if caller supplied fromProjId)
  //   4. single owner-match when ONLY one exists (ambiguous but better than null)
  const nameMatches = ownerCandidates.filter((c) => c.nameMatch)
  if (nameMatches.length > 0) {
    const accepted = nameMatches.find((c) => c.status === 'accepted' && c.projId != null)
    return (accepted ?? nameMatches[0])!.token
  }
  const idMatches = ownerCandidates.filter((c) => c.projIdMatch)
  if (idMatches.length > 0) {
    const accepted = idMatches.find((c) => c.status === 'accepted')
    return (accepted ?? idMatches[0])!.token
  }
  if (ownerCandidates.length === 1) {
    return ownerCandidates[0]!.token
  }
  return null
}

// ── Post-accept messaging (msgshare__) ─────────────────────────────────────
// msgshare__ carries messages between the GC and Client after accept.
// coshare__ and paymshare__ have been removed — those paths now flow through
// portallink__ (schema-v2). See V1-parity rebuild Task 13.

export interface MsgShareDoc {
  id: string
  linkId: string
  fromUid: string
  toUid: string
  fromName?: string
  /** Sender-side project name — scopes the message to a specific project
   *  so two projects between the same pair of users don't cross-pollute
   *  their message threads. Added 2026-04-21; legacy docs may omit it. */
  projName?: string
  /** Sender-side project id — additional scope hint (sender's local id,
   *  not the receiver's). Added 2026-04-21; may be absent on legacy docs. */
  projId?: number
  text: string
  sentAt: number
  participantUids: string[]
}

// Messages — append-only thread per dealLink.
//
// IMPORTANT: `linkId` (deallink__<id>) is per-user-PAIR, not per-project. When
// two projects exist between the same pair of users, filtering by linkId alone
// mixes both projects' threads. Always pass `projName` when you have it and
// want to scope to a single project. Docs without `projName` (legacy, pre
// 2026-04-21) are included as a backwards-compat fallback only when `projName`
// is omitted entirely; passing `projName` enforces strict per-project scoping
// (legacy docs with no projName are NOT returned — this is the correct
// behavior for fixing cross-project leakage).
export async function listMyMessages(
  uid: string,
  linkId: string,
  projName?: string,
): Promise<MsgShareDoc[]> {
  const col = collection(db, 'shared')
  const q = query(col, where('participantUids', 'array-contains', uid))
  const snap = await getDocs(q)
  const wantName = projName?.trim().toLowerCase()
  return snap.docs
    .filter((d) => d.id.startsWith(`msgshare__${linkId}__`))
    .map((d) => ({ ...(d.data() as MsgShareDoc), id: d.id }))
    .filter((m) => {
      if (!wantName) return true // caller didn't scope — legacy behavior
      const docName = (m.projName || '').trim().toLowerCase()
      return docName === wantName
    })
    .sort((a, b) => (a.sentAt || 0) - (b.sentAt || 0))
}

export async function sendMessage(input: {
  linkId: string
  fromUid: string
  toUid: string
  fromName?: string
  /** Pass the sender-side project name so the receiver can scope this message
   *  to the right project. Without it, messages cross-pollute when the pair
   *  has multiple active projects. */
  projName?: string
  /** Sender-side project id (additional scope hint). */
  projId?: number
  text: string
}): Promise<string> {
  const id = `msgshare__${input.linkId}__${Date.now()}`
  const data: Record<string, unknown> = {
    id,
    linkId: input.linkId,
    fromUid: input.fromUid,
    toUid: input.toUid,
    fromName: input.fromName,
    projName: input.projName,
    projId: input.projId,
    text: input.text,
    sentAt: Date.now(),
    participantUids: [input.fromUid, input.toUid],
  }
  await setShared(id, data)
  return id
}

